import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { BACKEND_URL } from "@/config";
import { adminApi, formatError } from "@/lib/adminApi";
import { useSite } from "@/lib/content";
import { Checkbox, LiveIndicator, SaveButton, SectionHeader } from "@/pages/admin/ui";

const input = "w-full min-h-[44px] rounded-xl border border-line bg-white px-3 text-base";
const card = "rounded-2xl border border-line bg-white p-5 space-y-3";
const WEEKDAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
const num = (v) => (v === "" || v == null ? null : Number(v));

function NumField({ id, label, value, onChange, suffix }) {
  return (
    <label htmlFor={id} className="block text-sm font-semibold text-wood space-y-1">
      <span>{label}</span>
      <span className="flex items-center gap-2">
        <input id={id} type="number" inputMode="numeric" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`${id}-input`} />
        {suffix && <span className="text-ink-muted font-normal">{suffix}</span>}
      </span>
    </label>
  );
}

function PriceEditor({ id, label, price, onChange }) {
  return (
    <div className={card} data-testid={id}>
      <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-wood">{label}</p><LiveIndicator live={price.value != null && price.verified} testId={`${id}-live`} /></div>
      <NumField id={`${id}-value`} label="Fiyat" suffix="TL" value={price.value} onChange={(v) => onChange({ ...price, value: v })} />
      <Checkbox id={`${id}-verified`} label="Doğrulandı" checked={price.verified} onChange={(v) => onChange({ ...price, verified: v })} testId={`${id}-verified`} />
    </div>
  );
}

function AgeEditor({ id, label, age, onChange }) {
  return (
    <div className={card} data-testid={id}>
      <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-wood">{label}</p><LiveIndicator live={age.min != null && age.verified} testId={`${id}-live`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <NumField id={`${id}-min`} label="En küçük yaş" value={age.min} onChange={(v) => onChange({ ...age, min: v })} />
        <NumField id={`${id}-max`} label="En büyük yaş" value={age.max} onChange={(v) => onChange({ ...age, max: v })} />
      </div>
      <Checkbox id={`${id}-verified`} label="Doğrulandı" checked={age.verified} onChange={(v) => onChange({ ...age, verified: v })} testId={`${id}-verified`} />
    </div>
  );
}

function Integration({ c, setC, reload }) {
  const [key, setKey] = useState("");
  const call = async (fn, msg) => {
    try {
      const { data } = await fn();
      if (data.api_key) setKey(data.api_key);
      if (data.webhook_secret) setC({ ...c, webhook_secret: data.webhook_secret });
      toast.success(msg);
      if (!data.webhook_secret) reload();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success("Kopyalandı"));
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className={card} data-testid="tickets-api-key">
        <p className="text-sm font-semibold text-wood">Salt okunur API anahtarı</p>
        <p className="text-xs text-ink-muted">GET /api/integrations/visits?from=&to=&updated_since= · başlık: X-API-Key. Ad, telefon ve e-posta paylaşılmaz. Belgeler: <a className="underline" href={`${BACKEND_URL}/api/docs`} target="_blank" rel="noreferrer">/api/docs</a></p>
        <p className="text-sm" data-testid="tickets-api-key-status">{c.api_key_enabled ? `Etkin (${c.api_key_hint})` : "Kapalı – anahtar yok"}</p>
        {key && (
          <div className="rounded-xl bg-[#FEF3C7] p-3 text-sm space-y-2" data-testid="tickets-api-key-new">
            <p className="font-semibold text-[#78350F]">Bu anahtar yalnızca bir kez gösterilir:</p>
            <code className="block break-all">{key}</code>
            <button type="button" onClick={() => copy(key)} className="inline-flex items-center gap-1 text-sm font-semibold"><Copy className="h-4 w-4" aria-hidden="true" /> Kopyala</button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => call(() => adminApi.post("/admin/integrations/api-key"), "Yeni anahtar oluşturuldu")} className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-forest text-cream text-sm font-semibold" data-testid="tickets-api-key-generate"><KeyRound className="h-4 w-4" aria-hidden="true" /> {c.api_key_enabled ? "Yenile" : "Anahtar oluştur"}</button>
          {c.api_key_enabled && <button type="button" onClick={() => { setKey(""); call(() => adminApi.delete("/admin/integrations/api-key"), "Anahtar iptal edildi"); }} className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-line text-sm font-semibold" data-testid="tickets-api-key-revoke"><Trash2 className="h-4 w-4" aria-hidden="true" /> İptal et</button>}
        </div>
      </div>
      <div className={card} data-testid="tickets-webhook">
        <p className="text-sm font-semibold text-wood">Giden webhook</p>
        <p className="text-xs text-ink-muted">Olaylar: booking.created, checked_in, cancelled. İmza: X-Woodia-Signature = sha256=HMAC(secret, zaman damgası + "." + gövde).</p>
        <input type="url" placeholder="https://..." value={c.webhook_url || ""} onChange={(e) => setC({ ...c, webhook_url: e.target.value })} className={input} aria-label="Webhook adresi" data-testid="tickets-webhook-url" />
        <Checkbox id="webhook-enabled" label="Webhook açık" checked={c.webhook_enabled} onChange={(v) => setC({ ...c, webhook_enabled: v })} testId="tickets-webhook-enabled" />
        {c.webhook_secret && <p className="text-xs break-all" data-testid="tickets-webhook-secret">Gizli anahtar: <code>{c.webhook_secret}</code></p>}
        <button type="button" onClick={() => call(() => adminApi.post("/admin/integrations/webhook-secret"), "Gizli anahtar yenilendi")} className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-line text-sm font-semibold" data-testid="tickets-webhook-secret-regen"><RefreshCw className="h-4 w-4" aria-hidden="true" /> Gizli anahtarı yenile</button>
      </div>
    </div>
  );
}

export default function TicketsSection() {
  const { reload: reloadSite } = useSite();
  const [c, setC] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => adminApi.get("/admin/tickets/config").then(({ data }) => setC(data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  if (!c) return <p className="text-ink-muted">Yükleniyor…</p>;
  const set = (k) => (v) => setC({ ...c, [k]: v });
  const fix = (p) => ({ ...p, value: num(p.value) });
  const fixAge = (a) => ({ ...a, min: num(a.min), max: num(a.max) });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        booking_open: c.booking_open, adult_price: fix(c.adult_price), child_price: fix(c.child_price), vehicle_price: fix(c.vehicle_price),
        child_age: fixAge(c.child_age), free_age: fixAge(c.free_age), vehicle_enabled: c.vehicle_enabled,
        max_persons: num(c.max_persons), daily_capacity: num(c.daily_capacity), window_days: num(c.window_days),
        closed_dates: c.closed_dates, closed_weekdays: c.closed_weekdays, retention_days: num(c.retention_days),
        webhook_enabled: c.webhook_enabled, webhook_url: c.webhook_url || null,
      };
      const { data } = await adminApi.put("/admin/tickets/config", body);
      setC(data);
      await reloadSite();
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };
  const purge = async () => {
    try {
      const { data } = await adminApi.post("/admin/visits/purge");
      toast.success(`${data.anonymized} kayıt anonimleştirildi`);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const toggleDay = (i) => set("closed_weekdays")(c.closed_weekdays.includes(i) ? c.closed_weekdays.filter((x) => x !== i) : [...c.closed_weekdays, i]);

  return (
    <section data-testid="admin-section-bilet" className="space-y-8">
      <SectionHeader title="Bilet & Fiyat" text="Ön kayıt ancak “Ön kayıt açık” işaretliyse, kullanılan tüm fiyat ve yaş aralıkları doğrulanmışsa ve KVKK + giriş koşulları (Yasal sekmesi) doğrulanmışsa ziyaretçilere görünür. Ödeme girişte alınır." />
      <div className={`rounded-2xl p-5 ${c.booking_live ? "bg-[#ECFDF3] text-[#05603A]" : "bg-[#FFFBEB] text-[#78350F]"}`} data-testid="tickets-status" data-live={c.booking_live}>
        <p className="font-semibold">{c.booking_live ? "Ön kayıt canlıda açık." : "Ön kayıt canlıda kapalı."}</p>
        {!c.booking_live && <ul className="list-disc pl-5 mt-2 text-sm space-y-1" data-testid="tickets-blockers">{c.blockers.map((b) => <li key={b}>{b}</li>)}</ul>}
      </div>
      <div className={card}><Checkbox id="booking-open" label="Ön kayıt açık" checked={c.booking_open} onChange={set("booking_open")} testId="tickets-booking-open" /></div>

      <div>
        <h3 className="text-xl font-semibold text-wood mb-3">Fiyatlar (TL, girişte ödenir)</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <PriceEditor id="price-adult" label="Yetişkin" price={c.adult_price} onChange={set("adult_price")} />
          <PriceEditor id="price-child" label="Çocuk" price={c.child_price} onChange={set("child_price")} />
          <div className={card}>
            <p className="text-sm font-semibold text-wood">Ücretsiz çocuk</p>
            <p className="text-sm text-ink-muted">Her zaman 0 TL.</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <AgeEditor id="age-child" label="Çocuk yaş aralığı" age={c.child_age} onChange={set("child_age")} />
          <AgeEditor id="age-free" label="Ücretsiz çocuk yaş aralığı" age={c.free_age} onChange={set("free_age")} />
        </div>
        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <div className={card}>
            <Checkbox id="vehicle-enabled" label="Araç ücreti uygula (plaka girilen kayıtlara)" checked={c.vehicle_enabled} onChange={set("vehicle_enabled")} testId="tickets-vehicle-enabled" />
          </div>
          {c.vehicle_enabled && <PriceEditor id="price-vehicle" label="Araç ücreti" price={c.vehicle_price} onChange={set("vehicle_price")} />}
        </div>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-wood mb-3">Kurallar</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className={card}><NumField id="tickets-max-persons" label="Kayıt başına en fazla kişi" value={c.max_persons} onChange={set("max_persons")} /></div>
          <div className={card}><NumField id="tickets-capacity" label="Günlük kapasite" suffix="kişi" value={c.daily_capacity} onChange={set("daily_capacity")} /></div>
          <div className={card}><NumField id="tickets-window" label="Kayıt penceresi (bugünden itibaren)" suffix="gün" value={c.window_days} onChange={set("window_days")} /></div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <fieldset className={card} data-testid="tickets-weekdays">
            <legend className="sr-only">Kapalı günler</legend>
            <p className="text-sm font-semibold text-wood">Kapalı haftanın günleri</p>
            <div className="flex flex-wrap gap-x-4">{WEEKDAYS.map((d, i) => <Checkbox key={d} id={`wd-${i}`} label={d} checked={c.closed_weekdays.includes(i)} onChange={() => toggleDay(i)} testId={`tickets-weekday-${i}`} />)}</div>
          </fieldset>
          <div className={card} data-testid="tickets-closed-dates">
            <p className="text-sm font-semibold text-wood">Kapalı tarihler</p>
            <div className="flex gap-2">
              <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className={input} aria-label="Kapalı tarih" data-testid="tickets-closed-date-input" />
              <button type="button" onClick={() => { if (newDate) { set("closed_dates")([...new Set([...c.closed_dates, newDate])].sort()); setNewDate(""); } }} className="min-h-[44px] px-4 rounded-full bg-forest text-cream text-sm font-semibold" data-testid="tickets-closed-date-add">Ekle</button>
            </div>
            <ul className="flex flex-wrap gap-2">
              {c.closed_dates.map((d) => (
                <li key={d}><button type="button" onClick={() => set("closed_dates")(c.closed_dates.filter((x) => x !== d))} className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-3 py-1.5 text-sm" aria-label={`${d} kaldır`}>{d.split("-").reverse().join(".")} <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /></button></li>
              ))}
              {!c.closed_dates.length && <li className="text-sm text-ink-muted">Yok</li>}
            </ul>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-wood mb-3">KVKK – veri saklama</h3>
        <div className={`${card} max-w-xl`}>
          <NumField id="tickets-retention" label="Kişisel veriler ziyaret tarihinden kaç gün sonra anonimleştirilsin?" suffix="gün" value={c.retention_days} onChange={set("retention_days")} />
          <p className="text-xs text-ink-muted">Ad, telefon, e-posta ve plaka silinir; sayılar ve tutarlar istatistik için kalır. Otomatik iş 6 saatte bir çalışır.</p>
          <button type="button" onClick={purge} className="min-h-[44px] px-4 rounded-full border border-line text-sm font-semibold" data-testid="tickets-purge-now">Şimdi anonimleştir</button>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-semibold text-wood mb-3">Dış sistem entegrasyonu (varsayılan kapalı)</h3>
        <Integration c={c} setC={setC} reload={load} />
      </div>
      <SaveButton onClick={save} saving={saving} testId="admin-save-bilet" />
    </section>
  );
}
